import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Link,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from '../../../contexts/AuthContext';
import MainLayout from '../../../components/layout/MainLayout';
import { validateInvitationToken, linkStudentWithInvitation } from '../../../services/teacherService';
import { RegisterData, ParentRegisterData, TeacherRegisterData } from '../../../types/auth';
import { getDefaultRouteForUser } from '../../../utils/roleNavigation';
import AccountTypeSelectionStep from './components/AccountTypeSelection';
import StudentAccountStep from './components/student/StudentAccountStep';
import StudentPersonalStep from './components/student/StudentPersonalStep';
import StudentDetailsStep from './components/student/StudentDetailsStep';
import ParentAccountStep from './components/parent/ParentAccountStep';
import ParentPersonalStep from './components/parent/ParentPersonalStep';
import LinkStudentStep from './components/parent/LinkStudentStep';
import TeacherAccountStep from './components/teacher/TeacherAccountStep';
import TeacherPersonalStep from './components/teacher/TeacherPersonalStep';

const studentSteps = ['Account Information', 'Personal Information', 'Student Details'];
const parentSteps = ['Account Information', 'Personal Information', 'Link Student'];
const teacherSteps = ['Account Information', 'Personal Information'];

function RegisterPage() {
  const { authState, register: registerUser, registerParent: registerParentUser, registerTeacher: registerTeacherUser, clearAuthError } = useAuth();
  const { error, loading, isAuthenticated, user } = authState;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeStep, setActiveStep] = useState(0);
  const [accountType, setAccountType] = useState<'student' | 'parent' | 'teacher' | null>(null);
  const [showAccountTypeSelection, setShowAccountTypeSelection] = useState(true);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<{teacherName?: string; schoolName?: string} | null>(null);
  const [linkingStatus, setLinkingStatus] = useState<'idle' | 'validating' | 'linking' | 'success' | 'error'>('idle');
  const [linkingError, setLinkingError] = useState<string | null>(null);

  const methods = useForm<RegisterData | ParentRegisterData | TeacherRegisterData>({
    defaultValues: accountType === 'parent' ? {
      parentData: {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: ''
      },
      childOption: 'existing',
      childData: {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        username: '',
        school: '',
        grade: undefined
      }
    } : accountType === 'teacher' ? {
      teacherData: {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        school: '',
        phone: ''
      }
    } : {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      profile: {
        username: '',
        school: '',
        grade: undefined,
        parentEmail: ''
      }
    },
    mode: 'onChange'
  });

  const { handleSubmit, trigger } = methods;

  // Track if we've already submitted the form and registration success
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Handle account type selection
  const handleAccountTypeSelect = (type: 'student' | 'parent' | 'teacher') => {
    setAccountType(type);
    setShowAccountTypeSelection(false);
    setActiveStep(0);

    // Reset form with appropriate default values for the selected account type
    const defaultValues = type === 'parent' ? {
      parentData: {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: ''
      },
      childOption: 'existing',
      childData: {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        username: '',
        school: '',
        grade: undefined
      }
    } : type === 'teacher' ? {
      teacherData: {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        school: '',
        phone: ''
      }
    } : {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      profile: {
        username: '',
        school: '',
        grade: undefined,
        parentEmail: ''
      }
    };

    methods.reset(defaultValues);

    // Clear any previous errors
    if (error) {
      clearAuthError();
    }
  };

  // Unified invitation handling effect
  useEffect(() => {
    const token = searchParams.get('invite');

    // If no token, check if we should redirect authenticated users
    if (!token) {
      if (isAuthenticated && user && !formSubmitted) {
        const redirectPath = getDefaultRouteForUser(user);
        navigate(redirectPath, { replace: true });
      }
      return;
    }

    // Store token and auto-select student account type
    if (inviteToken !== token) {
      setInviteToken(token);
      if (!accountType) {
        setAccountType('student');
        setShowAccountTypeSelection(false);
      }
    }

    // Handle invitation flow for logged-in students
    if (isAuthenticated && user) {
      // Check if user is a student
      if (user.role !== 'student') {
        setLinkingStatus('error');
        setLinkingError('This invitation link is for students only. Please log out and create a student account, or use your current account.');
        return;
      }

      // Proceed with linking flow
      const handleLoggedInStudentInvitation = async () => {
        try {
          // Step 1: Validate token
          if (linkingStatus === 'idle') {
            setLinkingStatus('validating');
            const info = await validateInvitationToken(token);

            if (!info.isValid) {
              setLinkingStatus('error');
              setLinkingError('This invitation link is invalid or has expired.');
              return;
            }

            setInviteInfo({ teacherName: info.teacherName, schoolName: info.schoolName });

            // Step 2: Link student to teacher
            setLinkingStatus('linking');
            await linkStudentWithInvitation(token);

            // Step 3: Success
            setLinkingStatus('success');

            // Redirect after 2 seconds
            setTimeout(() => {
              const redirectPath = getDefaultRouteForUser(user);
              navigate(redirectPath, { replace: true });
            }, 2000);
          }
        } catch (error) {
          console.error('Error in invitation flow:', error);
          const errorMessage = error instanceof Error ? error.message : 'An error occurred while connecting to your teacher.';
          setLinkingStatus('error');
          setLinkingError(errorMessage);
        }
      };

      handleLoggedInStudentInvitation();
    } else {
      // Not logged in - validate token for registration flow
      if (linkingStatus === 'idle') {
        setLinkingStatus('validating');
        validateInvitationToken(token)
          .then(info => {
            if (info.isValid) {
              setInviteInfo({ teacherName: info.teacherName, schoolName: info.schoolName });
              setLinkingStatus('idle'); // Reset for registration flow
            } else {
              setLinkingStatus('error');
              setLinkingError('This invitation link is invalid or has expired.');
            }
          })
          .catch(() => {
            setLinkingStatus('error');
            setLinkingError('Failed to validate invitation link.');
          });
      }
    }
  }, [searchParams, isAuthenticated, user, navigate, formSubmitted, inviteToken, accountType, linkingStatus]);

  // Clear error on unmount
  useEffect(() => {
    return () => {
      if (error) {
        clearAuthError();
      }
    };
  }, [error, clearAuthError]);

  // Reset success state if there's an error
  useEffect(() => {
    if (error) {
      setRegistrationSuccess(false);
    }
  }, [error]);

  // Get fields to validate for the current step
  const getFieldsToValidate = useCallback((step: number): string[] => {
    if (accountType === 'parent') {
      switch (step) {
        case 0:
          return ['parentData.email', 'parentData.password', 'parentData.confirmPassword'];
        case 1:
          return ['parentData.firstName', 'parentData.lastName'];
        case 2:
          return ['childOption', 'childData.email'];
        default:
          return [];
      }
    } else if (accountType === 'teacher') {
      switch (step) {
        case 0:
          return ['teacherData.email', 'teacherData.password', 'teacherData.confirmPassword'];
        case 1:
          return ['teacherData.firstName', 'teacherData.lastName'];
        default:
          return [];
      }
    } else {
      // Student validation
      switch (step) {
        case 0:
          return ['email', 'password', 'confirmPassword'];
        case 1:
          return ['firstName', 'lastName'];
        case 2:
          return ['profile.username', 'profile.school', 'profile.grade', 'profile.parentEmail'];
        default:
          return [];
      }
    }
  }, [accountType]);

  const handleNext = async () => {
    const fieldsToValidate = getFieldsToValidate(activeStep);

    // Validate the fields for the current step
    // Type assertion needed because we use a union type for the form
    const isStepValid = await trigger(fieldsToValidate as never);

    if (isStepValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = async (data: RegisterData | ParentRegisterData | TeacherRegisterData) => {
    try {
      setFormSubmitted(true);
      setRegistrationSuccess(false);

      if (error) {
        clearAuthError();
      }

      // Call appropriate registration method based on account type
      if (accountType === 'parent') {
        await registerParentUser(data as ParentRegisterData);
      } else if (accountType === 'teacher') {
        await registerTeacherUser(data as TeacherRegisterData);
      } else {
        await registerUser(data as RegisterData);
      }

      if (!error) {
        setRegistrationSuccess(true);
        setTimeout(() => {
          setFormSubmitted(false);
        }, 3000);
      } else {
        setFormSubmitted(false);
      }
    } catch (err) {
      setFormSubmitted(false);
      setRegistrationSuccess(false);
    }
  };

  // Render step content
  const getStepContent = (step: number) => {
    if (showAccountTypeSelection) {
      return <AccountTypeSelectionStep onSelectType={handleAccountTypeSelect} />;
    }

    if (accountType === 'parent') {
      switch (step) {
        case 0:
          return <ParentAccountStep />;
        case 1:
          return <ParentPersonalStep />;
        case 2:
          return <LinkStudentStep />;
        default:
          return 'Unknown step';
      }
    } else if (accountType === 'teacher') {
      switch (step) {
        case 0:
          return <TeacherAccountStep />;
        case 1:
          return <TeacherPersonalStep />;
        default:
          return 'Unknown step';
      }
    } else {
      switch (step) {
        case 0:
          return <StudentAccountStep />;
        case 1:
          return <StudentPersonalStep />;
        case 2:
          return <StudentDetailsStep />;
        default:
          return 'Unknown step';
      }
    }
  };

  // Early return: Show dedicated UI for logged-in students with invitation token
  if (isAuthenticated && user && inviteToken) {
    return (
      <MainLayout maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <CalculateIcon
              sx={{
                color: 'primary.main',
                fontSize: 40,
                mr: 1
              }}
            />
            <Typography component="h1" variant="h4" color="primary" fontWeight="bold">
              MathWiz League
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', width: '100%' }}>
            {linkingStatus === 'validating' && (
              <>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Validating Invitation...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we verify your teacher's invitation.
                </Typography>
              </>
            )}

            {linkingStatus === 'linking' && (
              <>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Connecting to {inviteInfo?.teacherName || 'Teacher'}...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Setting up your connection. This will only take a moment.
                </Typography>
              </>
            )}

            {linkingStatus === 'success' && (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'success.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <Typography variant="h2" color="success.dark">
                    ✓
                  </Typography>
                </Box>
                <Typography variant="h5" gutterBottom color="success.main" fontWeight="bold">
                  Successfully Connected!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  You are now linked to {inviteInfo?.teacherName || 'your teacher'}.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Redirecting to your dashboard...
                </Typography>
              </>
            )}

            {linkingStatus === 'error' && (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'error.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <Typography variant="h2" color="error.dark">
                    ✕
                  </Typography>
                </Box>
                <Typography variant="h5" gutterBottom color="error.main" fontWeight="bold">
                  Connection Failed
                </Typography>
                <Alert severity="error" sx={{ mt: 2, mb: 3, textAlign: 'left' }}>
                  {linkingError}
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const redirectPath = getDefaultRouteForUser(user);
                      navigate(redirectPath);
                    }}
                  >
                    Go to Dashboard
                  </Button>
                  {user.role === 'student' && (
                    <Button
                      variant="contained"
                      onClick={() => {
                        setLinkingStatus('idle');
                        setLinkingError(null);
                      }}
                    >
                      Try Again
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </MainLayout>
    );
  }

  return (
    <MainLayout maxWidth={showAccountTypeSelection ? "md" : "sm"}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <CalculateIcon
            sx={{
              color: 'primary.main',
              fontSize: { xs: 32, sm: 36, md: 40 },
              mr: 1
            }}
          />
          <Typography component="h1" variant="h4" color="primary" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            MathWiz League
          </Typography>
        </Box>

        <Typography component="h2" variant="h5" sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' }, textAlign: 'center' }}>
          {showAccountTypeSelection
            ? 'Create Your Account'
            : accountType === 'parent'
              ? 'Create Parent Account'
              : accountType === 'teacher'
                ? 'Create Teacher Account'
                : 'Create Student Account'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'center', px: { xs: 1, sm: 0 } }}>
          Join the math community today and start participating in our exciting competitions!
        </Typography>

        {!showAccountTypeSelection && (
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              width: '100%',
              mb: { xs: 2, sm: 3, md: 4 },
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mt: { xs: 0.5, sm: 1 }
              }
            }}
          >
            {(accountType === 'parent' ? parentSteps : accountType === 'teacher' ? teacherSteps : studentSteps).map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {error && !registrationSuccess && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        {registrationSuccess && !error && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            Registration successful! Your profile has been created. Redirecting to dashboard...
          </Alert>
        )}

        <FormProvider {...methods} key={accountType || 'selection'}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
            {getStepContent(activeStep)}

            {!showAccountTypeSelection && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: { xs: 2, sm: 3 }, gap: 1 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={() => {
                    if (activeStep === 0) {
                      setShowAccountTypeSelection(true);
                      setAccountType(null);
                    } else {
                      handleBack();
                    }
                  }}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  Back
                </Button>
                <Box>
                  {activeStep === (accountType === 'parent' ? parentSteps : accountType === 'teacher' ? teacherSteps : studentSteps).length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      color={accountType === 'parent' ? 'secondary' : accountType === 'teacher' ? 'success' : 'primary'}
                      disabled={loading}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Register'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color={accountType === 'parent' ? 'secondary' : accountType === 'teacher' ? 'success' : 'primary'}
                      onClick={handleNext}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </FormProvider>

        <Grid container justifyContent="center" sx={{ mt: { xs: 2, sm: 3 } }}>
          <Grid item>
            <Link component={RouterLink} to="/login" variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
              Already have an account? Sign in
            </Link>
          </Grid>
        </Grid>
      </Paper>
    </MainLayout>
  );
}

export default RegisterPage;
